import { type DefaultContext, mutationPromise } from '@egw/clients'
import { createMutationHook, createQueryHook } from '../../../../hooks'
import {
  type EnrollmentMultiResendReturnVerifyEmailSubmitInput,
  type EnrollmentMultiReturnVerifyEmailSubmitOutput,
  type EnrollmentMultiReturnVerifyEmailData,
  type EnrollmentMultiReturnVerifyEmailSubmitInput,
} from '../../../../models/enrollment/multi/return-verify-email'

/**
 * Get Enrollment Multi ReturnVerifyEmail data
 * @param input - ReentryInput
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiReturnVerifyEmailData>
 * @example const response = await getEnrollmentMultiReturnVerifyEmailData()
 */
export const getEnrollmentMultiReturnVerifyEmailData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiReturnVerifyEmailData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiReturnVerifyEmailData',
      query: `mutation getEnrollmentMultiReturnVerifyEmailData($input: ReentryInput!) {
        enterApplication(input: $input) {
          email
          nextStep
          error
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { enterApplication: EnrollmentMultiReturnVerifyEmailData }
  return response.enterApplication
}

export const useGetEnrollmentMultiReturnVerifyEmailData = createQueryHook<
  undefined,
  EnrollmentMultiReturnVerifyEmailData
>(getEnrollmentMultiReturnVerifyEmailData)

/**
 * Submit Enrollment Multi ReturnVerifyEmail data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiReturnVerifyEmailSubmitOutput>
 * @example const response = await submitEnrollmentMultiReturnVerifyEmailData();
 */
export const submitEnrollmentMultiReturnVerifyEmailData = async (
  enrollmentReturnVerifyEmailSubmitInput: EnrollmentMultiReturnVerifyEmailSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiReturnVerifyEmailSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiReturnVerifyEmailData',
      query: `mutation submitEnrollmentMultiReturnVerifyEmailData($input: ReentryInput!) {
        enterApplication(input: $input) {
          email
          nextStep
          error
        }
      }`,
    },
    {
      input: enrollmentReturnVerifyEmailSubmitInput,
    },
    context,
  )) as { enterApplication: EnrollmentMultiReturnVerifyEmailSubmitOutput }
  return response.enterApplication
}

export const useSubmitEnrollmentMultiReturnVerifyEmailData = createMutationHook<
  EnrollmentMultiReturnVerifyEmailSubmitInput,
  EnrollmentMultiReturnVerifyEmailSubmitOutput
>(submitEnrollmentMultiReturnVerifyEmailData)

/**
 * Submit Enrollment Multi ReturnVerifyEmail data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiReturnVerifyEmailSubmitOutput>
 * @example const response = await submitEnrollmentMultiReturnVerifyEmailData();
 */
export const submitEnrollmentMultiResendReturnVerifyEmailData = async (
  enrollmentResendReturnVerifyEmailSubmitInput: EnrollmentMultiResendReturnVerifyEmailSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiReturnVerifyEmailSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiResendReturnVerifyEmailData',
      query: `mutation submitEnrollmentMultiResendReturnVerifyEmailData($input: ReentryInput!) {
        enterApplication(input: $input) {
          email
          nextStep
          error
        }
      }`,
    },
    {
      input: enrollmentResendReturnVerifyEmailSubmitInput,
    },
    context,
  )) as { enterApplication: EnrollmentMultiReturnVerifyEmailSubmitOutput }
  return response.enterApplication
}

export const useSubmitEnrollmentMultiResendReturnVerifyEmailData = createMutationHook<
  EnrollmentMultiResendReturnVerifyEmailSubmitInput,
  EnrollmentMultiReturnVerifyEmailSubmitOutput
>(submitEnrollmentMultiResendReturnVerifyEmailData)
